import React, { useState } from 'react';
import { Alert } from '@mui/material';
import LateralPanel from '../../../../../muiComponents/LateralPanel';
import {
    useContractDetail,
    useContractDetailData,
} from '../../../../contextsProperty/ContextContractDetail';
import useTranslate from '../../../../../customHooks/useTranslate';
import SwitchInput from '../../../../../muiComponents/switchInput/SwitchInput';
import IconButton from '../../../../../muiComponents/IconButton';
import Grid from '../../../../../muiComponents/Grid';
import GridChild from '../../../../../muiComponents/GridChild';
import {
    doubleMD_ColumnGrid,
    gridSpacing20,
    gridSpacing30,
} from '../../../../../utils/commonVariables/grid';
import LoadedOptionsTypeahead from '../../../../../muiComponents/LoadedOptionsTypeahead';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import Title from '../../../../components/Title';
import Button from '../../../../../muiComponents/Button';
import { useODataMultipleQueries } from '../../../../../customHooks/useODataMultipleQueries';
import { readOnlyEntities } from '../../../../../utils/commonVariables/entityKeys';
import { ISupplierHotelRoomType } from '../../../../../types/entities/ISupplierHotelRoomType';
import Tooltip from '../../../../../muiComponents/Tooltip';
import Icon from '../../../../../muiComponents/Icon';
import { ROOM_VALIDATION_INITIAL_STATE } from '../../../../../utils/contractUtils/contractUtils';
import { validateForm } from '../../../../../utils/validateForms';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import DeletePopUp from '../DeletePopUp';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import LoadingPanel from './LoadingPanel';
import PanelTitle from '../../../yourProperties/Title';
import useUserProfile from '../../../../../customHooks/useUserProfile';
import ReadOnlyUserAlert from '../../../ReadOnlyUserAlert';

interface RoomPanelProps {
    open: boolean;
    onClose: () => void;
}

const RoomPanel = (props: RoomPanelProps) => {
    const { open, onClose } = props;
    const translate = useTranslate();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { readOnlyUser } = useUserProfile();
    const [deletePopUpVisible, setDeletePopUpVisible] = useState(false);

    const {
        isLoadingPanel,
        handleLoadingPanel,
        isBlockedContract,
        roomEdition,
        handleChangeRoomEdition,
        handleSaveRoom,
    } = useContractDetail();
    const { idSupplierHotel, rooms } = useContractDetailData();

    const isNewRoom = !roomEdition.id || parseInt(roomEdition.id) <= 0;

    const [validatedForm, setValidatedForm] = useState(false);
    const [formErrors, setFormErrors] = useState(ROOM_VALIDATION_INITIAL_STATE);

    const fieldsToValidate = {
        name: {
            value: roomEdition.name,
            validators: ['requiredString'],
        },
        idSupplierHotelRoomType: {
            value: roomEdition.idSupplierHotelRoomType,
            validators: ['requiredIdEntity'],
        },
    };

    const validateInputsForm = () => {
        setFormErrors(ROOM_VALIDATION_INITIAL_STATE);
        const errors = validateForm(fieldsToValidate);
        if (errors.length) {
            for (const error of errors) {
                setFormErrors(prev => ({
                    ...prev,
                    [error.inputKey]: error.errorMessage,
                }));
            }
        }
        return errors.length === 0;
    };

    const { supplierHotelRooms } = useODataMultipleQueries({
        queries: [
            {
                key: 'supplierHotelRooms',
                endpoint: {
                    entityName: readOnlyEntities.SupplierHotelRoomType,
                    select: 'Id,Name,AdultOnly,StandardOccupancy,MaxOccupancy,Occupancy,IdRateScopeModel,RateScopeModel/LocalizedCaption,RateScopeModel/Caption,RateScopeModel/Name,RateScopeModel/Key,MinAdults,MaxAdults,MinChildren,MaxChild',
                    orderBy: 'Name',
                    expand: 'RateScopeModel',
                    filter: [
                        `IdSupplierHotel eq ${idSupplierHotel}`,
                        'Enabled',
                    ],
                },
            },
        ],
        dependencies: [idSupplierHotel],
        condition: !!idSupplierHotel,
    });

    const remainingRoomOptions = supplierHotelRooms.data.filter(
        (supHotRoom: ISupplierHotelRoomType) =>
            !rooms.some(
                room => room.idSupplierHotelRoomType === parseInt(supHotRoom.Id)
            ) ||
            (!isNewRoom &&
                parseInt(supHotRoom.Id) === roomEdition.idSupplierHotelRoomType)
    );

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        try {
                            handleLoadingPanel(true);
                            await handleSaveRoom({
                                ...roomEdition,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingPanel(false);
                            setDeletePopUpVisible(false);
                            onClose();
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible(false);
                    }}
                ></DeletePopUp>
            )}
            <LateralPanel open={open}>
                {isLoadingPanel ? (
                    <LoadingPanel />
                ) : (
                    <form className='flex-column height100perc flex-between'>
                        <div className='flex-column  gap-30'>
                            <div className='flex-between flex-align-center contract-panel__title'>
                                <PanelTitle
                                    text={
                                        isNewRoom
                                            ? 'new_room'
                                            : roomEdition.name
                                    }
                                    className='text-secondary-color'
                                    translated={isNewRoom}
                                />
                                <div className='flex-between gap-10'>
                                    <SwitchInput
                                        labelText={''}
                                        checked={roomEdition.enabled}
                                        disabled={
                                            isBlockedContract || readOnlyUser
                                        }
                                        onChangeMethod={() => {
                                            handleChangeRoomEdition({
                                                enabled: !roomEdition.enabled,
                                            });
                                        }}
                                    />
                                    <IconButton
                                        iconName='Close'
                                        onClick={onClose}
                                    />
                                </div>
                            </div>

                            {readOnlyUser ? (
                                <ReadOnlyUserAlert text='read_only_contract' />
                            ) : (
                                isBlockedContract && (
                                    <Alert severity='info'>
                                        {translate(
                                            'bloqued_information_contract'
                                        )}
                                    </Alert>
                                )
                            )}
                            <div className='flex-column  gap-10 '>
                                <Grid
                                    className='border-color-top padding-top-20'
                                    style={{ width: 'auto' }}
                                    columnSpacing={gridSpacing30}
                                    rowSpacing={gridSpacing20}
                                >
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <LoadedOptionsTypeahead
                                            required
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            id='idSupplierMealPlan'
                                            gridProps={doubleMD_ColumnGrid}
                                            options={remainingRoomOptions}
                                            labelText='room'
                                            idValue={
                                                roomEdition.idSupplierHotelRoomType
                                            }
                                            handleChange={(
                                                _event: React.SyntheticEvent,
                                                value: ISupplierHotelRoomType | null
                                            ) => {
                                                handleChangeRoomEdition({
                                                    name: value?.Name || '',
                                                    idSupplierHotelRoomType:
                                                        value
                                                            ? parseInt(value.Id)
                                                            : undefined,
                                                    supplierHotelRoomType:
                                                        value?.Name || '',
                                                    standardOccupancy:
                                                        value?.StandardOccupancy ||
                                                        0,
                                                    maxOccupancy:
                                                        value?.MaxOccupancy ||
                                                        0,
                                                    minAdults:
                                                        value?.MinAdults || 0,
                                                    maxAdults:
                                                        value?.MaxAdults || 0,
                                                    minChildren:
                                                        value?.MinChildren || 0,
                                                    maxChildren:
                                                        value?.MaxChild || 0,
                                                    occupancy:
                                                        value?.Occupancy || '',
                                                    rateScopeModel:
                                                        value?.RateScopeModel
                                                            ?.LocalizedCaption ||
                                                        value?.RateScopeModel
                                                            ?.Caption ||
                                                        value?.RateScopeModel
                                                            ?.Name ||
                                                        '',
                                                    rateScopeModelKey:
                                                        value?.RateScopeModel
                                                            ?.Key || '',
                                                    idRateScopeModel:
                                                        value?.IdRateScopeModel ||
                                                        0,
                                                    adultsOnly:
                                                        value?.AdultOnly ||
                                                        false,
                                                    __isChanged: true,
                                                });
                                            }}
                                            error={
                                                !!formErrors.idSupplierHotelRoomType
                                            }
                                            errorMessage={
                                                formErrors.idSupplierHotelRoomType
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                        />
                                    </GridChild>
                                    <GridChild gridProps={doubleMD_ColumnGrid}>
                                        <TextInput
                                            required
                                            id='name'
                                            labelText='name'
                                            value={roomEdition.name}
                                            disabled={
                                                isLoadingPanel ||
                                                isBlockedContract ||
                                                readOnlyUser
                                            }
                                            handleChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                            ) =>
                                                handleChangeRoomEdition({
                                                    name: e.target.value,
                                                    __isChanged: true,
                                                })
                                            }
                                            onBlur={() => {
                                                if (validatedForm) {
                                                    validateInputsForm();
                                                }
                                            }}
                                            error={!!formErrors.name}
                                            errorMessage={formErrors.name}
                                        />
                                    </GridChild>
                                </Grid>

                                {roomEdition.idSupplierHotelRoomType ? (
                                    <Grid
                                        className='border-color-block'
                                        style={{
                                            width: 'auto',
                                            paddingTop: '10px',
                                            marginTop: '20px',
                                        }}
                                        columnSpacing={gridSpacing30}
                                        rowSpacing={gridSpacing20}
                                    >
                                        <GridChild
                                            gridProps={doubleMD_ColumnGrid}
                                        >
                                            <Title>
                                                {translate(
                                                    'standard_occupancy'
                                                )}
                                            </Title>
                                            <p>
                                                {roomEdition.standardOccupancy}
                                            </p>
                                        </GridChild>
                                        <GridChild
                                            gridProps={doubleMD_ColumnGrid}
                                        >
                                            <Title>
                                                {translate('max_occupancy')}
                                            </Title>
                                            <p>{roomEdition.maxOccupancy}</p>
                                        </GridChild>
                                        <GridChild
                                            gridProps={doubleMD_ColumnGrid}
                                        >
                                            <Title>
                                                {translate('occupancy_code')}
                                            </Title>
                                            <p className='text-overflow-ellipsis'>
                                                {roomEdition.occupancy}
                                            </p>
                                        </GridChild>
                                        <GridChild
                                            gridProps={doubleMD_ColumnGrid}
                                        >
                                            <Title>
                                                {translate('rate_scope_model')}
                                            </Title>

                                            {roomEdition.rateScopeModel ? (
                                                <Tooltip titleText='rate_scope_model'>
                                                    <p>
                                                        {
                                                            roomEdition.rateScopeModel
                                                        }
                                                    </p>
                                                </Tooltip>
                                            ) : (
                                                <div className='flex gap-10 flex-align-center'>
                                                    <Tooltip titleText='rate_scope_model'>
                                                        <p className='text-overflow-ellipsis'>
                                                            {'----'}
                                                        </p>
                                                    </Tooltip>
                                                    <Tooltip titleText='general_rate_scope_applied'>
                                                        <div>
                                                            <Icon
                                                                iconName={
                                                                    'InfoOutlined'
                                                                }
                                                                sx={{
                                                                    color: '#ffc107',
                                                                    width: '16px',
                                                                    height: '16px',
                                                                    cursor: 'pointer',
                                                                }}
                                                            />
                                                        </div>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </GridChild>
                                    </Grid>
                                ) : null}
                            </div>
                        </div>
                        <div
                            className={`flex-${isNewRoom ? 'end' : 'between'}`}
                        >
                            {!isNewRoom && (
                                <Button
                                    className='delete-button'
                                    onClick={() => {
                                        setDeletePopUpVisible(true);
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('delete')}
                                </Button>
                            )}

                            <div className='flex-end gap-20'>
                                <Button
                                    variant='outlined'
                                    onClick={onClose}
                                    disabled={isLoadingPanel}
                                >
                                    {translate('cancel')}
                                </Button>
                                <Button
                                    variant='contained'
                                    onClick={async () => {
                                        setValidatedForm(true);
                                        const validated = validateInputsForm();
                                        if (validated) {
                                            await handleSaveRoom();
                                        }
                                    }}
                                    disabled={
                                        isLoadingPanel ||
                                        isBlockedContract ||
                                        readOnlyUser
                                    }
                                >
                                    {translate('save')}
                                </Button>
                            </div>
                        </div>
                    </form>
                )}
            </LateralPanel>
        </>
    );
};

export default RoomPanel;
